import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  Fade,
  Divider,
  Avatar
} from '@mui/material';
import logoImage from '../../assets/images/er13.png';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ArrowForward,
} from '@mui/icons-material';
import { loginUser, getCurrentUser, clearError } from '../../store/slices/authSlice';
import PublicHeader from '../../components/Navigation/PublicHeader';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error } = useSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());
    
    const result = await dispatch(loginUser(formData));
    if (result.type === 'auth/login/fulfilled') {
      const userResult = await dispatch(getCurrentUser());
      if (userResult.type === 'auth/getCurrentUser/fulfilled') {
        const userRole = userResult.payload?.role;
        if (userRole === 'admin') {
          navigate('/admin');
        } else if (userRole === 'owner') {
          navigate('/owner/dashboard');
        } else {
          // Redirect clients back to home or a profile page
          navigate('/');
        }
      }
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F7F7F7', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />
      
      <Container maxWidth="xs" sx={{ mt: 12, mb: 4 }}>
        <Fade in={true} timeout={800}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              borderRadius: '24px', 
              border: '1px solid #EBEBEB',
              boxShadow: '0 8px 32px rgba(0,0,0,0.05)'
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Avatar
                src={logoImage}
                alt="CarryIT"
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '12px',
                  mx: 'auto',
                  mb: 2,
                }}
                variant="rounded"
              />
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#222' }}>
                Client Sign In
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Book stays and manage your rentals
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                {typeof error === 'string' ? error : 'Authentication failed'}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                margin="normal"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={handleChange}
                margin="normal"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={{
                  mt: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  bgcolor: '#ff385c',
                  fontWeight: 800,
                  fontSize: '1rem',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#e31c5f' }
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
              </Button>
            </Box>

            <Divider sx={{ my: 4 }}>
              <Typography variant="body2" color="text.secondary">or</Typography>
            </Divider>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Don't have an account?
              </Typography>
              <Link component={RouterLink} to="/register" sx={{ color: '#222', fontWeight: 800, textDecoration: 'underline' }}>
                Create account
              </Link>
              
              <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #F0F0F0' }}>
                <Link component={RouterLink} to="/owner-login" sx={{ color: '#717171', fontSize: '0.8rem', fontWeight: 500 }}>
                  Property Owner? Login here
                </Link>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default Login;
